import { useCallback, useEffect, useRef, useState } from "react";
import { api, asApiError, type ApiError } from "./client";

export interface QueryState<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  reload: () => Promise<void>;
}

export function useApiQuery<T>(path: string | null): QueryState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(path !== null);
  const sequence = useRef(0);
  const reload = useCallback(async (): Promise<void> => {
    if (path === null) { setLoading(false); return; }
    const current = ++sequence.current;
    setLoading(true);
    setError(undefined);
    try {
      const next = await api.get<T>(path);
      if (sequence.current === current) setData(next);
    } catch (caught) {
      if (sequence.current === current) setError(asApiError(caught));
    } finally {
      if (sequence.current === current) setLoading(false);
    }
  }, [path]);
  useEffect(() => { void reload(); return () => { sequence.current += 1; }; }, [reload]);
  return { data, error, loading, reload };
}

export interface MutationState {
  error: ApiError | undefined;
  loading: boolean;
  clearError: () => void;
}

export function useApiMutation<TArgs extends unknown[], TResult>(operation: (...args: TArgs) => Promise<TResult>): MutationState & { mutate: (...args: TArgs) => Promise<TResult | undefined> } {
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(false);
  const mutate = useCallback(async (...args: TArgs): Promise<TResult | undefined> => {
    setLoading(true);
    setError(undefined);
    try {
      return await operation(...args);
    } catch (caught) {
      setError(asApiError(caught));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [operation]);
  return { mutate, error, loading, clearError: () => setError(undefined) };
}
